<template>
    <div class="invoice-scan">
        <v-app-bar
            color="#1d1f29"
            app
            dark
        >
            {{ $t(`invoice_scan.${mode}`) }}

            <v-spacer />

            <v-menu bottom transition="slide-y-transition">
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon>
                        <v-icon>far fa-ellipsis-v</v-icon>
                    </v-btn>
                </template>

                <v-list class="user-menu">
                    <v-list-item
                        key="qrcode"
                        :class="{ 'primary--text': mode === 'qrcode' }"
                        @click="mode = 'qrcode'"
                    >
                        <v-list-item-action class="minw20 ml-1">
                            <v-icon :class="{ 'primary--text': mode === 'qrcode' }">
                                fal fa-qrcode
                            </v-icon>
                        </v-list-item-action>
                        <v-list-item-title>
                            {{ $t('invoice_scan.qrcode') }}
                        </v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        key="photo"
                        :class="{ 'primary--text': mode === 'photo' }"
                        @click="mode = 'photo'"
                    >
                        <v-list-item-action class="minw20 ml-1">
                            <v-icon :class="{ 'primary--text': mode === 'photo' }">
                                fal fa-camera
                            </v-icon>
                        </v-list-item-action>
                        <v-list-item-title>
                            {{ $t('invoice_scan.photo') }}
                        </v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        key="link"
                        :class="{ 'primary--text': mode === 'link' }"
                        @click="mode = 'link'"
                    >
                        <v-list-item-action class="minw20 ml-1">
                            <v-icon :class="{ 'primary--text': mode === 'link' }">
                                fal fa-link
                            </v-icon>
                        </v-list-item-action>
                        <v-list-item-title>
                            {{ $t('invoice_scan.link') }}
                        </v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </v-app-bar>

        <v-main>
            <!-- Top Progress Bar -->
            <v-progress-linear
                v-if="busy"
                class="loading"
                color="#1565c0"
                indeterminate
                absolute
            />

            <div
                v-if="mode === 'qrcode'"
                class="camera-container"
            >
                <qrcode-stream
                    :torch="true"
                    :track="paintBoundingBox"
                    :camera="camera"
                    @init="onInit"
                    @decode="onDecode"
                >
                    <div v-if="result === 'success'" class="validation-success">
                        {{ $t('invoice_scan.found') }}
                    </div>

                    <div v-if="result === 'failed'" class="validation-failure">
                        {{ $t('invoice_scan.error.not_valid') }}
                    </div>

                    <div v-if="result === 'alreadyAdded'" class="validation-failure">
                        {{ $t('invoice_scan.error.already_added') }}
                    </div>

                    <div v-if="!result && camera === 'off'" class="validation-pending">
                        {{ $t('invoice_scan.checking') }}
                    </div>
                </qrcode-stream>
            </div>

            <div
                v-else-if="mode === 'photo'"
                class="pa-5"
            >
                <qrcode-capture
                    ref="qrCapture"
                    accept="image/*"
                    capture="user"
                    @decode="onDecode"
                />
            </div>

            <div
                v-else-if="mode === 'link'"
                class="pa-5"
            >
                <div class="d-flex align-center">
                    <v-text-field
                        v-model="link"
                        :label="$t('invoice_scan.link')"
                        hide-details
                    />

                    <v-btn
                        class="ml-2 mt-4"
                        text
                        :loading="busy"
                        :disabled="!link || !link.startsWith('https://')"
                        @click="onDecode(link)"
                    >
                        {{ $t('general.add') }}
                    </v-btn>
                </div>

                <v-alert
                    v-if="result && result !== 'success'"
                    class="mt-5"
                    type="error"
                    transition="slide-y-transition"
                    text
                >
                    <span v-if="result === 'alreadyAdded'">
                        {{ $t('invoice_scan.error.already_added') }}
                    </span>
                    <span v-else>
                        {{ $t('invoice_scan.error.not_valid') }}
                    </span>
                </v-alert>
            </div>
        </v-main>

        <div
            v-if="receipts.length"
            class="footer"
        >
            <v-alert
                class="alert"
                type="info"
                :color="busy ? 'warning' : 'info'"
                transition="slide-y-transition"
                dark
                prominent
            >
                <div class="d-flex align-items-center">
                    <div>{{ $t('invoice_scan.detected') }}: {{ receipts.length }}</div>

                    <v-spacer />

                    <v-btn
                        outlined
                        :loading="busy"
                        :disabled="busy"
                        @click="saveOrders"
                    >
                        <v-icon class="mr-2">fal fa-check</v-icon>
                        {{ $t('general.save') }}
                    </v-btn>
                </div>
            </v-alert>
        </div>
    </div>
</template>

<script>
// import { QrcodeStream, QrcodeDropZone, QrcodeCapture } from 'vue-qrcode-reader'
import { QrcodeStream } from 'vue-qrcode-reader'
import ApiService from '@/services/ApiService';

export default {
    beforeRouteEnter(to, from, next) {
        if(!to.params.token) {
            next(from?.name ? from : false);
            return;
        }

        ApiService.post('/orders/import/init', { token: to.params.token }).then(response => {
            if(!response.data.hash) {
                next(from?.name ? from : false);
                return;
            }

            next(vm => {
                vm.from = from?.name;
                vm.onDataLoaded(response.data);
            })
        });
    },

    components: {
        QrcodeStream,
        // QrcodeDropZone,
        // QrcodeCapture
    },

    created() {
    },

    mounted() {},

    data() {
        return {
            from: null,
            busy: false,
            venueHash: null,
            camera: 'auto',
            receipts: [],
            result: null,
            mode: 'qrcode',
            link: null
        };
    },

    watch: {
        mode(val) {
            this.result = null;
        }
    },

    methods: {
        onDataLoaded(data) {
            this.venueHash = data.hash;
        },

        onInit (promise) {
            promise
                .catch(console.error)
                .then(() => {
                    this.result = null;
                })
        },

        async onDecode(decodedString) {
            this.busy = true;

            this.turnCameraOff();

            // pretend it's taking really long
            await this.timeout(2000)

            if(this.receipts.includes(decodedString)) {
                this.result = 'alreadyAdded';
            } else {
                const url = {
                    production: [
                        'https://mapr.tax.gov.me/ic/#/verify',
                        'https://suf.purs.gov.rs/v/?vl'
                    ],
                    staging: [
                        'https://efitest.tax.gov.me/ic/#/verify',
                        'https://sandbox.suf.purs.gov.rs/v/?vl'
                    ]
                }

                const isProduction = process.env.VUE_APP_ENV === 'production';
                const isValid = isProduction
                    ? url.production.some(url => !!decodedString?.startsWith(url))
                    : [...url.production, ...url.staging].some(url => !!decodedString?.startsWith(url));

                this.result = isValid ? 'success' : 'failed';
            }

            if(this.result === 'success') {
                this.receipts.push(decodedString);
            }

            // some more delay, so users have time to read the message
            await this.timeout(1500)

            this.turnCameraOn()

            if(this.$refs?.qrCapture?.$el) {
                this.$refs.qrCapture.$el.value = ''
            }

            this.link = null;
            this.busy = false;
        },

        turnCameraOn () {
            this.camera = 'auto'
        },

        turnCameraOff () {
            this.camera = 'off'
        },

        timeout (ms) {
            return new Promise(resolve => {
                window.setTimeout(resolve, ms)
            })
        },

        paintBoundingBox (detectedCodes, ctx) {
            for (const detectedCode of detectedCodes) {
                const { boundingBox: { x, y, width, height } } = detectedCode

                ctx.lineWidth = 2
                ctx.strokeStyle = '#007bff'
                ctx.strokeRect(x, y, width, height)
            }
        },

        async saveOrders() {
            this.busy = true;

            const response = await ApiService.post('/orders/import', {
                hash: this.venueHash,
                receipts: this.receipts,
            })

            this.busy = false;
            this.receipts = [];

            if(!response.data.success) {
                const { message } = response.data;
                switch (message) {
                    case 'wrong_seller':
                        this.$snackbar(this.$t('invoice_scan.error.wrong_seller'), 'error');
                        break;
                    case 'unfiscalized_order':
                        this.$snackbar(this.$t('invoice_scan.error.unfiscalized_order'), 'error');
                        break;
                    case 'order_exists':
                        this.$snackbar(this.$t('invoice_scan.error.order_exists'), 'error');
                        break;
                    default:
                        this.$snackbar(this.$t('invoice_scan.error.save_failed'), 'error');
                        break;
                }
                return;
            }

            if(this.from) {
                this.$router.push({ name: this.from }).catch(err => {});
            }

            this.$snackbar(this.$t('invoice_scan.saved'), 'success');
        }
    }
};
</script>

<style lang="scss" scoped>
.invoice-scan {
    height: 100%;
    position: relative;

    -webkit-user-select: none;
    -webkit-app-region: drag;

    .v-main {
        height: 100%;

        .camera-container {
            background-color: black;
            height: 100%;
            width: 100%;
        }
    }

    .validation-success,
    .validation-failure,
    .validation-pending {
        position: absolute;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, .8);
        text-align: center;
        font-size: 2rem;
        display: flex;
        flex-flow: column nowrap;
        justify-content: center;
    }
    .validation-success {
        color: green;
    }
    .validation-failure {
        color: red;
    }

    .bottom-nav {
        background-color: white !important;

        .nav-option {
            max-width: 200px;
            font-size: 14px;
            word-break: break-word;
            white-space: break-spaces;

            ::v-deep .v-btn__content {
                max-width: 150px;
            }
        }
    }

    .footer {
        position: fixed;
        bottom: 0;
        width: 100%;

        .alert {
            border-radius: 0;
            width: 100%;
            margin: 0;
            opacity: 0.8;
        }
    }
}
</style>
